// ============================================================================
// AdminTableControls - floating admin panel on tournament tables
// ============================================================================
// Visible only to admins on tables that belong to a tournament. Surfaces the
// levers needed to actually exercise tournament features end-to-end:
//   - Force-start (skip pre-game wait)
//   - Advance blind level (jump to next street of antes)
//   - Balance tables (trigger rebalancer for table-merge testing)
//   - Cancel (refund real players, end the tournament)
// Designed to sit in the bottom-right as a collapsible card so it doesn't
// occlude the table during normal play.
// ============================================================================
import React, { useState } from 'react';
import {
  Box, VStack, HStack, Button, Text, IconButton, Badge,
} from '@chakra-ui/react';
import {
  Settings, ChevronDown, ChevronUp, Play, FastForward, Shuffle, Skull, XCircle,
} from 'lucide-react';
import gameApiService from '../../services/gameApiService';
import { POKER_COLORS } from '../../theme/designSystem';

// startTournament writes status='running'. The admin panel used to check
// 'active' (and only 'active'), which permanently disabled every control
// on every started tournament. Accept the full set of "tournament is live
// and admin actions make sense" statuses.
function isLiveTournamentStatus(status?: string): boolean {
  return status === 'running'
    || status === 'active'
    || status === 'in_progress'
    || status === 'late_reg'
    || status === 'final_table';
}

function isStartableStatus(status?: string): boolean {
  return status === 'registering'
    || status === 'scheduled'
    || status === 'upcoming';
}

interface AdminTableControlsProps {
  tournamentId: string;
  tableId?: string;            // current table the admin is viewing (for force-fold)
  tournamentStatus?: string;
  currentBlindLevel?: number;
  currentPlayerName?: string;  // shown next to Force Fold so admin knows who's about to be folded
  simStage?: string;           // live stage of the one-click full sim (from tournament.simPlan)
}

const SIM_STAGE_LABELS: Record<string, string> = {
  break: '1/7 Break (30s)',
  moveMe: '2/7 Moving YOU to a new table',
  bustMe: '3/7 Busting YOU',
  rebuyMe: '4/7 Rebuying YOU + deal-in',
  bubble: '5/7 Hand-for-hand bubble',
  money: '6/7 In the money',
  finalTable: '7/7 Final table merge',
  done: 'Complete - final table live',
};

const AdminTableControls: React.FC<AdminTableControlsProps> = ({
  tournamentId, tableId, tournamentStatus, currentBlindLevel, currentPlayerName, simStage,
}) => {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const flash = (ok: boolean, text: string) => {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const doStart = async () => {
    if (!window.confirm('Force-start the tournament now?')) return;
    setBusy('start');
    const r = await gameApiService.adminStartTournament(tournamentId);
    setBusy(null);
    flash(r.success, r.success
      ? `Started - ${r.playerCount || 0} players across ${r.tables?.length || 0} tables`
      : (r.error || 'Start failed'));
  };

  const doAdvance = async () => {
    setBusy('advance');
    const r = await gameApiService.adminAdvanceBlindLevel(tournamentId);
    setBusy(null);
    flash(r.success, r.success
      ? `L${r.previousLevel} → L${r.newLevel}: ${r.blinds?.smallBlind}/${r.blinds?.bigBlind}${r.blinds?.ante ? ` ante ${r.blinds.ante}` : ''}`
      : (r.error || 'Advance failed'));
  };

  const doBalance = async () => {
    setBusy('balance');
    const r: any = await gameApiService.adminBalanceTables(tournamentId);
    setBusy(null);
    flash(r.success, r.success
      ? (r.message || `${r.moves?.length || 0} player moves across ${r.tablesBalanced || 0} tables`)
      : (r.error || 'Balance failed'));
  };

  const doForceFold = async () => {
    if (!tableId) return;
    setBusy('forceFold');
    const r = await gameApiService.adminForceFoldCurrentPlayer(tableId);
    setBusy(null);
    flash(r.success, r.success
      ? `Force-folded ${r.foldedPlayerName || r.foldedPlayerId?.substring(0, 8)}`
      : (r.error || 'Force fold failed'));
  };

  const doCancel = async () => {
    if (!window.confirm('Cancel the entire tournament? Real entrants will be refunded.')) return;
    setBusy('cancel');
    const r = await gameApiService.adminCancelTournament(tournamentId);
    setBusy(null);
    flash(r.success, r.success ? 'Tournament cancelled' : (r.error || 'Cancel failed'));
  };

  return (
    <Box
      position="fixed"
      bottom="20px"
      right="20px"
      zIndex={9100}
      bg="rgba(13, 31, 46, 0.95)"
      border="1px solid"
      borderColor={`${POKER_COLORS.cyan}55`}
      borderRadius="lg"
      backdropFilter="blur(10px)"
      boxShadow="0 8px 32px rgba(0,0,0,0.5)"
      minW="260px"
      maxW="320px"
    >
      <HStack
        px={3} py={2}
        cursor="pointer"
        onClick={() => setOpen(o => !o)}
        borderBottom={open ? '1px solid' : 'none'}
        borderColor={`${POKER_COLORS.cyan}22`}
      >
        <Settings size={14} color={POKER_COLORS.cyan} />
        <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" letterSpacing="wide" flex={1}>
          ADMIN CONTROLS
        </Text>
        {tournamentStatus && (
          <Badge
            fontSize="9px"
            colorScheme={tournamentStatus === 'active' ? 'green' :
                         tournamentStatus === 'cancelled' ? 'red' : 'cyan'}
          >
            {tournamentStatus}
          </Badge>
        )}
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Toggle"
          icon={open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          color="gray.400"
        />
      </HStack>

      {open && (
        <VStack align="stretch" spacing={2} p={3}>
          {currentBlindLevel !== undefined && (
            <Text fontSize="xs" color="gray.500">
              Current blind level: <Text as="span" color="white" fontWeight="bold">L{currentBlindLevel}</Text>
            </Text>
          )}

          <Button
            size="sm"
            variant="outline"
            colorScheme="green"
            leftIcon={<Play size={12} />}
            onClick={doStart}
            isLoading={busy === 'start'}
            isDisabled={busy !== null || !isStartableStatus(tournamentStatus)}
          >
            Force start now
          </Button>

          {tableId && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              leftIcon={<XCircle size={12} />}
              onClick={doForceFold}
              isLoading={busy === 'forceFold'}
              isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
              title="Use when a bot or disconnected player is stalling the hand"
            >
              Force fold {currentPlayerName ? `(${currentPlayerName})` : 'current player'}
            </Button>
          )}

          <Button
            size="sm"
            variant="outline"
            colorScheme="cyan"
            leftIcon={<FastForward size={12} />}
            onClick={doAdvance}
            isLoading={busy === 'advance'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
          >
            Advance blinds
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            leftIcon={<Shuffle size={12} />}
            onClick={doBalance}
            isLoading={busy === 'balance'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
          >
            Balance tables
          </Button>

          {/* 🎬 ONE-CLICK FULL SIM - the whole arc: break → H4H bubble →
              ITM → table merge → final table. Server-driven stage machine;
              this button just starts/stops it. */}
          <Button
            size="sm"
            colorScheme={simStage && simStage !== 'done' ? 'red' : 'yellow'}
            variant="solid"
            onClick={async () => {
              setBusy('simFull');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'runFullSim');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Full sim failed');
            }}
            isLoading={busy === 'simFull'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Runs the entire tournament arc on hyper with one click: 30s break, hand-for-hand bubble (2 rounds), bubble burst + ITM cinematic, bust-down + table merge, final table with seat redraw / blind rollback / 30s break. Hyper turns off when the final table forms so you can play it out."
          >
            {simStage && simStage !== 'done' ? '■ STOP FULL SIM' : '▶ RUN FULL SIM'}
          </Button>
          {simStage && (
            <Text fontSize="xs" color={simStage === 'done' ? '#4ade80' : '#fde68a'} textAlign="center" fontWeight="bold">
              Sim: {SIM_STAGE_LABELS[simStage] || simStage}
            </Text>
          )}

          {/* SIMULATION LEVERS - exercise every tournament mechanism on demand */}
          <Button
            size="sm"
            variant="outline"
            colorScheme="yellow"
            onClick={async () => {
              setBusy('simBreak');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'startBreak');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Break start failed');
            }}
            isLoading={busy === 'simBreak'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
          >
            Start break now
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="yellow"
            onClick={async () => {
              setBusy('simEndBreak');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'endBreak');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Break end failed');
            }}
            isLoading={busy === 'simEndBreak'}
            isDisabled={busy !== null}
          >
            End break now
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="orange"
            onClick={async () => {
              setBusy('simBust');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'bustBots', 5);
              setBusy(null);
              flash(r.success, r.message || r.error || 'Bust failed');
            }}
            isLoading={busy === 'simBust'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Eliminates 5 bots (between hands) - fast-forwards toward consolidation, the bubble, and payouts"
          >
            Bust 5 bots
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="teal"
            onClick={async () => {
              setBusy('simHyper');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'toggleHyper');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Hyper toggle failed');
            }}
            isLoading={busy === 'simHyper'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Near-instant bots + 0.8s hand boundaries - fast-forward the tournament to what you want to test. Click again for normal pacing."
          >
            Hyper speed
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="pink"
            onClick={async () => {
              setBusy('simBubble');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'bustToBubble');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Bubble sim failed');
            }}
            isLoading={busy === 'simBubble'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Busts bots down to paid+3 players - hand-for-hand engages at the next hand boundary"
          >
            Bubble: hand-for-hand
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="green"
            onClick={async () => {
              setBusy('simMoney');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'bustToMoney');
              setBusy(null);
              flash(r.success, r.message || r.error || 'ITM sim failed');
            }}
            isLoading={busy === 'simMoney'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Busts bots down to exactly the paid spots - the bubble bursts and the IN THE MONEY cinematic fires"
          >
            Burst bubble (ITM)
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="orange"
            onClick={async () => {
              setBusy('simBustMe');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'bustMe');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Bust-me failed');
            }}
            isLoading={busy === 'simBustMe'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Busts YOUR stack between hands so you can test the rebuy flow (Tournament Rebuy button appears; you're dealt back in next hand)"
          >
            Bust me (test rebuy)
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="yellow"
            onClick={async () => {
              setBusy('simFT');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'formFinalTable');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Final table failed');
            }}
            isLoading={busy === 'simFT'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Merges every remaining table onto one: feeders drain as hands finish, seats redraw at random, shallow blinds roll back, and a 30s final-table break runs (with the cinematic). Fires automatically when the field fits one table - this button forces it."
          >
            Form final table
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="cyan"
            onClick={async () => {
              setBusy('simRollback');
              const r: any = await gameApiService.adminTournamentSimulate(tournamentId, 'rollbackBlinds');
              setBusy(null);
              flash(r.success, r.message || r.error || 'Rollback failed');
            }}
            isLoading={busy === 'simRollback'}
            isDisabled={busy !== null || !isLiveTournamentStatus(tournamentStatus)}
            title="Rolls blinds back one level and restarts the level clock - the TD move when final-table stacks are shallow. Applies from the next hand."
          >
            Roll back blinds
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            leftIcon={<Skull size={12} />}
            onClick={doCancel}
            isLoading={busy === 'cancel'}
            isDisabled={busy !== null || tournamentStatus === 'cancelled' || tournamentStatus === 'completed'}
          >
            Cancel tournament
          </Button>

          {msg && (
            <Box
              bg={msg.ok ? `${POKER_COLORS.cyan}22` : 'red.900'}
              border="1px solid"
              borderColor={msg.ok ? `${POKER_COLORS.cyan}55` : 'red.700'}
              borderRadius="sm"
              p={2}
            >
              <Text fontSize="xs" color={msg.ok ? POKER_COLORS.cyan : 'red.200'}>{msg.text}</Text>
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
};

export default AdminTableControls;
